import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { requestOtpApi, verifyOtpApi } from '../http/http'; // Removed polling API
import toast, { Toaster } from 'react-hot-toast'; 
import classes from './OtpPage.module.css';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 29;

const OtpPage = () => {
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
    const [error, setError] = useState('');
    const [timer, setTimer] = useState(RESEND_SECONDS);
    const [loading, setLoading] = useState(false);
    const inputRefs = useRef([]);

    const navigate = useNavigate();
    const location = useLocation();
    const phone = location.state?.phone || 'XXXXXXXXXX';
    const ext_ref = location.state?.ext_ref || '';
    const [referenceNo, setReferenceNo] = useState(location.state?.referenceNo || '');
    

    useEffect(() => {
        if (timer === 0) return;
        const interval = setInterval(() => {
            setTimer((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => clearInterval(interval);
    }, [timer]);

useEffect(() => {
  const otpRequested = localStorage.getItem("otpRequested");

if (!location.state?.phone || !otpRequested) {
    navigate("/login", { replace: true });
  }
}, []);

    const handleChange = (value, index) => {
        if (!/^\d*$/.test(value)) return;
        const newOtp = [...otp];
        newOtp[index] = value.slice(-1);
        setOtp(newOtp);
        setError('');

        if (value && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
        const newOtp = [...otp];
        pasted.split('').forEach((char, i) => { newOtp[i] = char; });
        setOtp(newOtp);
        inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    };

  const handleVerify = async (e) => {
        e.preventDefault();
        setError('');
        const enteredOtp = otp.join('');
        if (enteredOtp.length < OTP_LENGTH) {
            toast.error("Please enter the complete 6-digit code");
            return;
        }

        setLoading(true);
        const verifyToastId = toast.loading("Verifying code...");
        
        try {
            const response = await verifyOtpApi(phone, referenceNo, enteredOtp, ext_ref);
            
            // Accessing the nested data based on your console log
            const resData = response.data?.data || response.data;
            const statusCode = resData?.statusCode;
            const subStatus = resData?.subscriptionStatus;

            // S1000 is the success code from your backend log
            if (statusCode === 'S1000') {
                localStorage.getItem('otpRequested') && localStorage.removeItem('otpRequested');
                
                
                if (subStatus === 'REGISTERED' || subStatus === 'INITIAL_CHARGING_PENDING') {
                    toast.success("Verification successful!", { id: verifyToastId });
                    
                    // Store user data for protected routes & unsubscribe
                    const userData = {
                        msisdn: phone,
                        masked_msisdn: resData?.subscriberId || '',
                        status: subStatus
                    };
                    localStorage.setItem('userData', JSON.stringify(userData));
                     if (response.data?.token) {
                        localStorage.setItem('token', response.data.token);
                    }

                    setTimeout(() => {
                        navigate('/confirmation'); 
                    }, 1500);

                } else if (subStatus === 'REG_PENDING') {
                    // This handles the case where the user has no balance
                    toast.error(
                        "OTP Verified! However, your balance is insufficient. Please top up to activate.", 
                        { id: verifyToastId, duration: 6000 }
                    );
                    
                    setTimeout(() => {
                        navigate('/login'); 
                    }, 6000);
                } else {
                    // Fallback for other successful status types
                    toast.success("Success!", { id: verifyToastId });
                    navigate('/confirmation');
                }

            } else {
                // If statusCode is not S1000
                const msg = resData?.statusDetail || resData?.message || 'Verification failed';
                toast.error(msg, { id: verifyToastId });
                setLoading(false);
            }
        } catch (err) {
            console.error("Verification Error:", err);
            const msg = err.response?.data?.message || 'Something went wrong';
            toast.error(msg, { id: verifyToastId });
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (timer > 0) return;
        setOtp(Array(OTP_LENGTH).fill(''));
        setLoading(true);
        
        const resendToast = toast.loading("Resending code...");
        
        try {
            const response = await requestOtpApi(phone, ext_ref);
            if (response.data && response.data.data) {
                setReferenceNo(response.data.data.referenceNo);
                toast.success("New code sent!", { id: resendToast });
            }
            setTimer(RESEND_SECONDS);
            inputRefs.current[0]?.focus();
        } catch (err) {
            toast.error("Failed to resend code", { id: resendToast });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={classes.page_wrapper}>
            <Toaster position="top-center" reverseOrder={false} />
            
            <div className={classes.bg_blob_1}></div>
            <div className={classes.bg_blob_2}></div>

            <div className={classes.card}>
                <div className={classes.logo_area}>
                    <div className={classes.logo_icon_wrap}>
                        <img src="/assets/images/logo.png" alt="logo" className={classes.logo_icon} />
                    </div>
                    <p className={classes.logo_text}>Game<span>Plex</span></p>
                </div>

                <div className={classes.heading_area}>
                    <h1 className={classes.title}>Verify Number</h1>
                    <p className={classes.subtitle}>
                        We sent a code to <span className={classes.phone_highlight}>+94 {phone}</span>
                    </p>
                </div>

                <form className={classes.form} onSubmit={handleVerify}>
                    <div className={classes.otp_row} onPaste={handlePaste}>
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => (inputRefs.current[index] = el)}
                                className={classes.otp_input}
                                type="tel"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleChange(e.target.value, index)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                disabled={loading}
                            />
                        ))}
                    </div>

                    <button type="submit" className={classes.submit_btn} disabled={loading}>
                        {loading ? 'Verifying...' : 'Verify & Subscribe'}
                    </button>
                </form>

                <p className={classes.resend_row}>
                    Didn't receive a code?{' '}
                    <button
                        type="button"
                        className={classes.resend_btn}
                        onClick={handleResend}
                        disabled={timer > 0 || loading}
                    >
                        {timer > 0
                            ? <span className={classes.resend_timer}>Resend ({timer}s)</span>
                            : 'Resend OTP'}
                    </button>
                </p>
            </div>
        </div>
    );
};

export default OtpPage;